import type { Work } from '../manifest';
import { pick } from '../i18n';

/** True when the work has a non-empty description in the current language. */
export function describable(work: Work): boolean {
  return pick(work.description).trim().length > 0;
}

type Mode = 'hidden' | 'teaser' | 'expanded';

/**
 * One reusable description frame. Presentation only — controls.ts decides when
 * to call teaser()/expand()/collapse()/hide(). teaser() positions the node near
 * the work; expand() switches it to a fixed right-side panel (CSS-driven).
 */
export class HoverFrame {
  private el: HTMLDivElement;
  private titleEl: HTMLDivElement;
  private bodyEl: HTMLParagraphElement;
  private mode: Mode = 'hidden';
  private work: Work | null = null;

  constructor() {
    this.el = document.createElement('div');
    this.el.className = 'work-frame';
    this.el.innerHTML = `<div class="wf-title"></div><p class="wf-body"></p>`;
    document.body.appendChild(this.el);
    this.titleEl = this.el.querySelector('.wf-title')!;
    this.bodyEl = this.el.querySelector('.wf-body')!;
  }

  private fill(work: Work): void {
    if (work === this.work) return;
    this.titleEl.textContent = pick(work.title);
    this.bodyEl.textContent = pick(work.description);
    this.el.style.setProperty('--accent', work.palette[0] ?? '#888888');
    this.work = work;
  }

  /** Teaser near projected screen coords (x, y); body clamped to 2 lines via CSS. */
  teaser(work: Work, x: number, y: number): void {
    this.fill(work);
    if (this.mode !== 'teaser') {
      this.el.classList.remove('expanded');
      this.el.classList.add('teaser', 'visible');
      this.mode = 'teaser';
    }
    this.el.style.transform = `translate(${x}px, ${y}px) translateX(-50%)`;
  }

  /** Fixed right-side panel with the full body (CSS positions it). */
  expand(work: Work): void {
    this.fill(work);
    if (this.mode === 'expanded') return;
    this.el.classList.remove('teaser');
    this.el.classList.add('expanded', 'visible');
    this.el.style.transform = ''; // let CSS .expanded place it
    this.mode = 'expanded';
  }

  hide(): void {
    if (this.mode === 'hidden') return;
    this.el.classList.remove('visible', 'teaser', 'expanded');
    this.mode = 'hidden';
    this.work = null;
  }
}
